<template>
  <v-dialog v-model="showDialog" width="700" scrollable persistent>
    <v-card>
      <v-card-title class="flex items-center">
        <span>{{ t("categories.manage.title") }}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :disabled="saving"
          @click="showDialog = false"
        ></v-btn>
      </v-card-title>

      <v-card-text style="max-height: 70vh">
        <div class="flex flex-col gap-4">
          <VueDraggable
            v-model="stagedGroups"
            tag="div"
            class="flex flex-col gap-4"
            handle=".group-handle"
            :animation="150"
            :disabled="saving"
            :scroll="true"
            :scroll-sensitivity="80"
            :scroll-speed="20"
            :bubble-scroll="true"
            :force-auto-scroll-fallback="true"
          >
            <div v-for="group in stagedGroups" :key="group.id" class="group-card p-3 rounded-lg">
              <div class="flex items-center gap-2 mb-2">
                <v-icon
                  icon="mdi-drag-vertical"
                  size="small"
                  class="group-handle cursor-grab text-medium-emphasis"
                ></v-icon>
                <v-icon
                  icon="mdi-folder-outline"
                  size="small"
                  class="text-medium-emphasis"
                ></v-icon>
                <span class="font-bold">{{ group.name }}</span>
                <span class="text-xs text-medium-emphasis">
                  {{ (categoriesByGroup[group.id] ?? []).length }}
                </span>
                <v-spacer></v-spacer>
                <v-btn
                  icon="mdi-pencil-outline"
                  size="small"
                  variant="text"
                  density="comfortable"
                  :disabled="saving"
                  @click="renameGroup(group)"
                ></v-btn>
                <v-btn
                  icon="mdi-trash-can-outline"
                  size="small"
                  variant="text"
                  density="comfortable"
                  :disabled="saving"
                  @click="deleteGroup(group)"
                ></v-btn>
              </div>

              <CategoryGroupSortable
                :model-value="categoriesByGroup[group.id] ?? []"
                :disabled="saving"
                @update:model-value="setCategoriesInGroup(group.id, $event)"
                @delete="deleteCategory($event)"
                @rename="renameCategory($event)"
              />

              <v-btn
                variant="text"
                size="small"
                prepend-icon="mdi-plus"
                class="mt-2 text-medium-emphasis"
                :disabled="saving"
                @click="addCategory(group.id)"
              >
                {{ t("categories.manage.addCategory") }}
              </v-btn>
            </div>
          </VueDraggable>

          <div class="group-card group-card--virtual p-3 rounded-lg">
            <div class="flex items-center gap-2 mb-2">
              <v-icon icon="mdi-folder-outline" size="small" class="text-medium-emphasis"></v-icon>
              <span class="font-bold text-medium-emphasis italic">
                {{ t("categories.manage.otherGroup") }}
              </span>
              <span class="text-xs text-medium-emphasis">
                {{ (categoriesByGroup[UNGROUPED_KEY] ?? []).length }}
              </span>
            </div>

            <CategoryGroupSortable
              :model-value="categoriesByGroup[UNGROUPED_KEY] ?? []"
              :disabled="saving"
              @update:model-value="setCategoriesInGroup(undefined, $event)"
              @delete="deleteCategory($event)"
              @rename="renameCategory($event)"
            />

            <v-btn
              variant="text"
              size="small"
              prepend-icon="mdi-plus"
              class="mt-2 text-medium-emphasis"
              :disabled="saving"
              @click="addCategory(undefined)"
            >
              {{ t("categories.manage.addCategory") }}
            </v-btn>
          </div>

          <v-btn
            prepend-icon="mdi-plus"
            variant="flat"
            color="surface-variant"
            class="self-start"
            :disabled="saving"
            @click="addGroup()"
          >
            {{ t("categories.manage.newGroup") }}
          </v-btn>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="px-4 py-3">
        <v-btn
          variant="text"
          color="error"
          :disabled="saving || (stagedCategories.length === 0 && stagedGroups.length === 0)"
          @click="showDeleteAllDialog = true"
        >
          {{ t("categories.manage.deleteAllButton") }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="flat" :loading="saving" :disabled="saving" @click="save">
          {{ t("common.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="showDeleteAllDialog" width="400">
      <v-card>
        <v-card-title>{{ t("categories.manage.deleteAllTitle") }}</v-card-title>
        <v-card-text>
          {{
            stagedCategories.length > 0
              ? t("categories.manage.deleteAllMessage", { count: stagedCategories.length })
              : t("categories.manage.deleteAllGroupsMessage")
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="saving" @click="showDeleteAllDialog = false">
            {{ t("common.cancel") }}
          </v-btn>
          <v-btn color="error" variant="flat" :disabled="saving" @click="confirmDeleteAll()">
            {{ t("common.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteGroupDialog" width="400">
      <v-card>
        <v-card-title>{{ t("categories.manage.deleteGroupTitle") }}</v-card-title>
        <v-card-text>
          {{
            t("categories.manage.deleteGroupMessage", {
              name: groupToDelete?.name ?? "",
              count: groupToDelete ? (categoriesByGroup[groupToDelete.id] ?? []).length : 0,
            })
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showDeleteGroupDialog = false">{{ t("common.cancel") }}</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDeleteGroup()">
            {{ t("common.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteCategoryDialog" width="400">
      <v-card>
        <v-card-title>{{ t("categories.manage.deleteCategoryTitle") }}</v-card-title>
        <v-card-text>
          {{
            t("categories.manage.deleteCategoryMessage", {
              name: categoryToDelete?.name ?? "",
            })
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showDeleteCategoryDialog = false">{{ t("common.cancel") }}</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDeleteCategory()">
            {{ t("common.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <EditGroupDialog
      v-model="showEditGroupDialog"
      :group="groupToEdit"
      :existing-groups="stagedGroups"
      @save="saveGroup($event)"
    />

    <EditCategoryDialog
      v-model="showEditCategoryDialog"
      :category="categoryToEdit"
      :existing-categories="stagedCategories"
      @save="saveCategoryEdit($event)"
    />
  </v-dialog>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useTrackedCategoryGroups } from "@/composables/useTrackedCategoryGroups";
import { call, db } from "@/firebase";
import { useToast } from "@/store";
import type {
  TrackedCategory,
  TrackedCategoryDirection,
  TrackedCategoryGroup,
} from "@aircap/common/trackedCategory";
import { captureException } from "@sentry/vue";
import { collection, deleteField, doc, serverTimestamp, writeBatch } from "firebase/firestore";
import { computed, ref, toRef, watch } from "vue";
import { VueDraggable } from "vue-draggable-plus";
import type { TrackedCategoryRow } from "../types";
import CategoryGroupSortable from "./CategoryGroupSortable.vue";
import EditCategoryDialog from "./EditCategoryDialog.vue";
import EditGroupDialog from "./EditGroupDialog.vue";

const props = defineProps<{
  accountId: string;
  direction: TrackedCategoryDirection;
  categories: TrackedCategoryRow[];
  currencyCode: string;
  seedCategories?: Array<{ name: string; amount: number; groupName?: string }>;
}>();

const emit = defineEmits<{ "seeds-consumed": [] }>();

const showDialog = defineModel<boolean>({ required: true });

const { t, locale } = useI18n();
const { showErrorToast } = useToast();

const { groups: groupsList, groupsRaw } = useTrackedCategoryGroups(
  toRef(() => props.accountId),
  props.direction,
);

// Array index is the source of truth for order — no separate field
interface StagedGroup {
  id: string; // existing doc id, or "new:<n>" for unsaved
  name: string;
}

interface StagedCategory {
  id: string; // existing doc id, or "new:<n>"
  name: string;
  amount: number;
  groupId: string | undefined;
}

const stagedGroups = ref<StagedGroup[]>([]);
const stagedCategories = ref<StagedCategory[]>([]);
// Snapshotted at dialog-open so concurrent writes from other tabs are not soft-deleted on save
const originalGroupIds = ref<Set<string>>(new Set());
const originalCategoryIds = ref<Set<string>>(new Set());

let nextLocalId = 1;
const newId = (prefix: "g" | "c") => `new:${prefix}${nextLocalId++}`;

const UNGROUPED_KEY = "";

const categoriesByGroup = computed(() => {
  const formatter = new Intl.NumberFormat(locale.value, {
    style: "currency",
    currency: props.currencyCode,
  });
  const byGroup: Record<string, Array<StagedCategory & { amountLabel: string }>> = {};
  for (const category of stagedCategories.value) {
    const key = category.groupId ?? UNGROUPED_KEY;
    byGroup[key] ??= [];
    byGroup[key].push({ ...category, amountLabel: formatter.format(category.amount) });
  }
  return byGroup;
});

const setCategoriesInGroup = (groupId: string | undefined, nextList: StagedCategory[]) => {
  const movedIds = new Set(nextList.map((c) => c.id));
  const others = stagedCategories.value.filter((c) => !movedIds.has(c.id));
  stagedCategories.value = [
    ...others,
    ...nextList.map((c) => ({ id: c.id, name: c.name, amount: c.amount, groupId })),
  ];
};

const resetStaged = () => {
  nextLocalId = 1;
  stagedGroups.value = groupsList.value.map((g) => ({ id: g.id, name: g.name }));
  stagedCategories.value = props.categories.map((c) => ({
    id: c.id,
    name: c.name,
    amount: c.amount,
    groupId: c.groupId,
  }));
  originalGroupIds.value = new Set(stagedGroups.value.map((g) => g.id));
  originalCategoryIds.value = new Set(stagedCategories.value.map((c) => c.id));

  if (props.seedCategories?.length) {
    const existingNames = new Set(stagedCategories.value.map((c) => c.name.toLowerCase().trim()));
    const groupIdByName = new Map<string, string>();
    for (const g of stagedGroups.value) {
      groupIdByName.set(g.name.toLowerCase().trim(), g.id);
    }

    for (const seed of props.seedCategories) {
      const seedName = seed.name.trim();
      if (!seedName || existingNames.has(seedName.toLowerCase())) continue;

      const seedGroupName = seed.groupName?.trim();
      let groupId: string | undefined;
      if (seedGroupName) {
        const key = seedGroupName.toLowerCase();
        groupId = groupIdByName.get(key);
        if (!groupId) {
          groupId = newId("g");
          groupIdByName.set(key, groupId);
          stagedGroups.value.push({ id: groupId, name: seedGroupName });
        }
      }

      stagedCategories.value.push({
        id: newId("c"),
        name: seedName,
        amount: seed.amount,
        groupId,
      });
      existingNames.add(seedName.toLowerCase());
    }
    emit("seeds-consumed");
  }
};

const showDeleteAllDialog = ref(false);

watch(showDialog, (value) => {
  if (value) {
    resetStaged();
  } else {
    showDeleteAllDialog.value = false;
  }
});

const showEditGroupDialog = ref(false);
const groupToEdit = ref<StagedGroup | null>(null);

const addGroup = () => {
  groupToEdit.value = null;
  showEditGroupDialog.value = true;
};

const renameGroup = (group: StagedGroup) => {
  groupToEdit.value = group;
  showEditGroupDialog.value = true;
};

watch(showEditGroupDialog, (open) => {
  if (!open) groupToEdit.value = null;
});

const saveGroup = (value: { name: string }) => {
  if (groupToEdit.value) {
    const target = stagedGroups.value.find((g) => g.id === groupToEdit.value?.id);
    if (target) target.name = value.name;
  } else {
    stagedGroups.value.push({ id: newId("g"), name: value.name });
  }
};

const showDeleteGroupDialog = ref(false);
const groupToDelete = ref<StagedGroup | null>(null);

const deleteGroup = (group: StagedGroup) => {
  groupToDelete.value = group;
  showDeleteGroupDialog.value = true;
};

const confirmDeleteGroup = () => {
  const group = groupToDelete.value;
  if (!group) return;
  for (const c of stagedCategories.value) {
    if (c.groupId === group.id) c.groupId = undefined;
  }
  stagedGroups.value = stagedGroups.value.filter((g) => g.id !== group.id);
  showDeleteGroupDialog.value = false;
  groupToDelete.value = null;
};

const showDeleteCategoryDialog = ref(false);
const categoryToDelete = ref<StagedCategory | null>(null);

const showEditCategoryDialog = ref(false);
const categoryToEdit = ref<StagedCategory | null>(null);
const pendingNewCategoryGroupId = ref<string | undefined>(undefined);

const renameCategory = (category: { id: string; name: string; amount: number }) => {
  const staged = stagedCategories.value.find((c) => c.id === category.id);
  if (staged) {
    categoryToEdit.value = staged;
    showEditCategoryDialog.value = true;
  }
};

const saveCategoryEdit = (value: { name: string; amount: number }) => {
  if (categoryToEdit.value) {
    const target = stagedCategories.value.find((c) => c.id === categoryToEdit.value?.id);
    if (target) {
      target.name = value.name;
      target.amount = value.amount;
    }
  } else {
    stagedCategories.value.push({
      id: newId("c"),
      name: value.name,
      amount: value.amount,
      groupId: pendingNewCategoryGroupId.value,
    });
  }
};

const deleteCategory = (category: { id: string }) => {
  const staged = stagedCategories.value.find((c) => c.id === category.id);
  if (staged) {
    categoryToDelete.value = staged;
    showDeleteCategoryDialog.value = true;
  }
};

const confirmDeleteCategory = () => {
  const target = categoryToDelete.value;
  if (!target) return;
  stagedCategories.value = stagedCategories.value.filter((c) => c.id !== target.id);
  showDeleteCategoryDialog.value = false;
  categoryToDelete.value = null;
};

const saving = ref(false);

const confirmDeleteAll = () => {
  if (saving.value) return;
  if (stagedCategories.value.length === 0 && stagedGroups.value.length === 0) return;
  stagedCategories.value = [];
  stagedGroups.value = [];
  showDeleteAllDialog.value = false;
};

const addCategory = (groupId: string | undefined) => {
  pendingNewCategoryGroupId.value = groupId;
  categoryToEdit.value = null;
  showEditCategoryDialog.value = true;
};

watch(showEditCategoryDialog, (open) => {
  if (!open) {
    pendingNewCategoryGroupId.value = undefined;
    categoryToEdit.value = null;
  }
});

const originalGroup = (id: string) => groupsRaw.value?.find((g) => g.id === id);
const originalCategory = (id: string) => props.categories.find((c) => c.id === id);

const save = async () => {
  if (saving.value) return;
  saving.value = true;

  // Genkit only uses category create/delete/name (direction is fixed per dialog).
  let runSuggestBackfill = false;
  for (const c of stagedCategories.value) {
    if (c.id.startsWith("new:")) {
      runSuggestBackfill = true;
      break;
    }
    const orig = originalCategory(c.id);
    if (orig && orig.name !== c.name) {
      runSuggestBackfill = true;
      break;
    }
  }
  if (!runSuggestBackfill) {
    const stagedIds = new Set(stagedCategories.value.map((c) => c.id));
    for (const id of originalCategoryIds.value) {
      if (!stagedIds.has(id)) {
        runSuggestBackfill = true;
        break;
      }
    }
  }

  try {
    const batch = writeBatch(db);
    const groupsCol = collection<TrackedCategoryGroup>(
      db,
      `accounts/${props.accountId}/trackedCategoryGroups`,
    );
    const categoriesCol = collection<TrackedCategory>(
      db,
      `accounts/${props.accountId}/trackedCategories`,
    );

    const groupIdRemap = new Map<string, string>();

    stagedGroups.value.forEach((g, order) => {
      if (g.id.startsWith("new:")) {
        const ref = doc(groupsCol);
        groupIdRemap.set(g.id, ref.id);
        batch.set(ref, {
          name: g.name,
          direction: props.direction,
          order,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        const orig = originalGroup(g.id);
        if (!orig) return;
        if (orig.name !== g.name || orig.order !== order) {
          batch.update(doc(groupsCol, g.id), {
            name: g.name,
            order,
            updatedAt: serverTimestamp(),
          });
        }
      }
    });

    const stagedGroupIds = new Set(stagedGroups.value.map((g) => g.id));
    for (const id of originalGroupIds.value) {
      if (!stagedGroupIds.has(id)) {
        batch.update(doc(groupsCol, id), {
          deletedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }

    const orderByGroup = new Map<string | undefined, number>();
    for (const c of stagedCategories.value) {
      const resolvedGroupId = c.groupId ? (groupIdRemap.get(c.groupId) ?? c.groupId) : undefined;
      const order = orderByGroup.get(resolvedGroupId) ?? 0;
      orderByGroup.set(resolvedGroupId, order + 1);

      if (c.id.startsWith("new:")) {
        const ref = doc(categoriesCol);
        batch.set(ref, {
          name: c.name,
          amount: c.amount,
          direction: props.direction,
          order,
          ...(resolvedGroupId ? { groupId: resolvedGroupId } : {}),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        const orig = originalCategory(c.id);
        if (!orig) continue;
        const changed =
          orig.name !== c.name ||
          orig.amount !== c.amount ||
          orig.groupId !== resolvedGroupId ||
          orig.order !== order;
        if (changed) {
          batch.update(doc(categoriesCol, c.id), {
            name: c.name,
            amount: c.amount,
            order,
            groupId: resolvedGroupId ?? deleteField(),
            updatedAt: serverTimestamp(),
          });
        }
      }
    }

    const stagedCategoryIds = new Set(stagedCategories.value.map((c) => c.id));
    for (const id of originalCategoryIds.value) {
      if (!stagedCategoryIds.has(id)) {
        batch.update(doc(categoriesCol, id), {
          deletedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }

    await batch.commit();
    showDialog.value = false;
  } catch (cause) {
    const error = new Error("Error saving tracked categories structure", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
    return;
  } finally {
    saving.value = false;
  }

  if (!runSuggestBackfill) return;
  try {
    await call("backfillTrackedCategorySuggestions")({ accountId: props.accountId });
  } catch (cause) {
    const error = new Error("Error backfilling tracked category suggestions", { cause });
    console.error(error);
    captureException(error);
  }
};
</script>

<style scoped>
.group-card {
  background-color: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.group-card--virtual {
  background-color: rgba(var(--v-border-color), 0.04);
}
.cursor-grab {
  cursor: grab;
}
.cursor-grab:active {
  cursor: grabbing;
}
</style>
